<template>
    <div>
        <div
            v-show="!selectedLibrary"
            class="block">
            Select a library to get example queries and information about available data columns.
        </div>
        <div class="block">
            <b-field
                label="Select library:">
                <b-autocomplete
                    v-model="input"
                    :data="filteredList"
                    placeholder="Library name"
                    :keep-first="true"
                    :open-on-focus="true"
                    icon="magnify"
                    :clearable="true"
                    @select="selectHandler">
                    <template #empty>
                        No library found
                    </template>
                </b-autocomplete>
            </b-field>
            <router-link
                v-if="selectedLibrary"
                :to="getLibraryLink(selectedLibrary)">
                Go to {{ selectedLibrary }} library page &raquo;
            </router-link>
        </div>
        <div
            v-if="exampleQueryGenerators"
            class="block">
            <b-field label="Example queries:">
                <b-button
                    v-for="example in exampleQueryGenerators"
                    :key="example.description"
                    type="is-info"
                    size="is-small"
                    icon-left="script-text-outline"
                    expanded
                    class="mb-1"
                    @click="loadExample(example)">
                    <span>{{ example.description }}</span>
                </b-button>
            </b-field>
        </div>
        <div
            v-if="sqlEditorStore.tables.length > 0"
            class="block">
            <b-field label="Recent table definitions:">
                <ul>
                    <li
                        v-for="table in sqlEditorStore.tables"
                        :key="table.view.library">
                        <div
                            class="is-flex is-justify-content-space-between library-node">
                            <div
                                class="is-clickable"
                                @click="table.isOpen = !table.isOpen; onClickRecentLibrary(table.view.library);">
                                <b-icon
                                    :title="table.isOpen ? 'Collapse' : 'Expand'"
                                    :icon="table.isOpen ? 'chevron-down' : 'chevron-right'"
                                    size="is-small" />
                                <b-icon
                                    icon="table"
                                    size="is-small"
                                    class="mr-1" />
                                <b>{{ table.view.library }}</b>
                            </div>
                            <div>
                                <b-icon
                                    title="Remove"
                                    icon="close-circle"
                                    size="is-small"
                                    class="is-clickable has-text-grey pt-auto pb-auto mx-1"
                                    @click="sqlEditorStore.tablesRemove(table.view.library)" />
                            </div>
                        </div>
                        <ul>
                            <li
                                v-for="(column, index) in table.view.columns"
                                v-show="table.isOpen"
                                :key="index"
                                class="ml-5">
                                <div>
                                    <b-tooltip
                                        :label="column.iconTooltip"
                                        :triggers="column.iconTooltip ? ['hover'] : []"
                                        position="is-right">
                                        <b-icon
                                            :icon="column.icon ?? 'table-column'"
                                            size="is-small" />
                                    </b-tooltip>
                                    {{ column.column_name }} <span class="has-text-grey">({{ column.typeSuffix }})</span>
                                </div>
                            </li>
                        </ul>
                    </li>
                </ul>
            </b-field>
        </div>
    </div>
</template>

<script>
    import { getLibraryLink, getSqlEditorLinkByLibrary } from '@/shared/helpers/routing';
    import { getExamples } from './sqlExampleGenerator.ts';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { loadLibraryDefinition } from '@/apps/sqlEditor/sqlEditor.libraryloading.ts';

    export default {
        name: 'SqlLibraryBrowser',
        emits: [
            'set-sql'
        ],
        data() {
            return {
                input: '',
                viewDefinition: null,
                attributeDefinitions: null,
                exampleQueryGenerators: null,
                sqlEditorStore: useSqlEditorStore()
            };
        },
        computed: {
            filteredList() {
                if (!this.input)
                    return this.sqlEditorStore.libraryNames;

                return this.sqlEditorStore.libraryNames.filter((option) =>
                    option
                        .toString()
                        .toLowerCase()
                        .indexOf(this.input.toLowerCase()) >= 0
                );
            },
            selectedLibrary() {
                return this.sqlEditorStore.libraryGetSelected();
            }
        },
        watch: {
            selectedLibrary: {
                async handler() {
                    const lib = this.selectedLibrary;
                    if (lib) {
                        const libdef = await loadLibraryDefinition(lib);
                        this.viewDefinition = libdef.view;
                        this.attributeDefinitions = libdef.attributes;

                        await this.sqlEditorStore.tablesAdd(this.viewDefinition, this.attributeDefinitions);
                        this.exampleQueryGenerators = getExamples(this.viewDefinition, this.attributeDefinitions);

                        this.input = lib;
                    } else {
                        this.viewDefinition = null;
                        this.attributeDefinitions = null;
                        this.exampleQueryGenerators = null;
                        this.input = '';
                    }
                },
                immediate: true
            }
        },
        methods: {
            loadExample(example) {
                const sql = example.makeQuery(this.viewDefinition, this.attributeDefinitions);
                this.$emit('set-sql', sql);
            },
            selectHandler(value) {
                if (!value) {
                    this.sqlEditorStore.librarySetSelected(null);
                }
                this.$router.push(getSqlEditorLinkByLibrary(value));
            },
            onClickRecentLibrary(library) {
                if (!this.selectedLibrary) {
                    this.selectedLibrary = this.sqlEditorStore.librarySetSelected(library);
                }
            },
            getLibraryLink
        }
    };
</script>

<style lang="css" scoped>
    .library-node:hover {
        background-color: #efefef;
    }
</style>
